/**
 * SecretInline — nod inline care arată o valoare secretă mascată cu eye reveal
 * cu auto-hide după timeout-ul configurat (preferences.getRevealTimeout).
 *
 * Flow:
 * 1. /secret slash command inserează nodul gol → render input auto-focused
 * 2. User scrie → Enter / blur → salvează valoarea în atributul node `value`
 * 3. Node arată „••••••" + iconă ochi + iconă creion (editare)
 * 4. Click eye → dezvăluie pentru X secunde → auto-mascare
 * 5. Click creion → revine în mod input pentru editare
 *
 * Securitate: valoarea trăiește plaintext în content-ul note-ului, dar
 * întreaga notă e criptată E2E (doar user cu master_key poate decripta).
 * Nu mai sigur decât textul normal din notă, dar adaugă protecție vizuală
 * (over-the-shoulder).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

import { getRevealTimeout } from '@/stores/preferences';

export const SecretInlineNode = Node.create({
  name: 'secretInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      value: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-value') || '',
        renderHTML: (attrs) => ({ 'data-value': attrs.value || '' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-secret-inline]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    // În renderHTML (export HTML), persistăm valoarea în atribut + content
    // text = mascat (pentru eventuale render-uri non-editor-iente, dacă
    // cineva exportează). Editorul propriu folosește NodeView.
    return [
      'span',
      mergeAttributes(
        {
          'data-secret-inline': '',
          'data-value': node.attrs.value || '',
        },
        HTMLAttributes,
      ),
      '••••••',
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SecretInlineView);
  },
});

function SecretInlineView({
  node,
  updateAttributes,
  editor,
  deleteNode,
}: NodeViewProps) {
  const { t } = useTranslation();
  const value = (node.attrs.value as string) || '';
  // Edit mode automat când nodul e gol (proaspăt inserat).
  const [editing, setEditing] = useState(value === '');
  const [revealed, setRevealed] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<number | null>(null);

  const isEditable = editor.isEditable;

  useEffect(() => {
    if (editing) {
      // Auto-focus input la intrare în mod edit
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [editing]);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const save = () => {
    const v = draft;
    if (v === '') {
      // Gol → ștergem nodul (nu păstrăm secret invalid)
      deleteNode();
      return;
    }
    updateAttributes({ value: v });
    setEditing(false);
  };

  const cancel = () => {
    if (value === '') {
      deleteNode();
    } else {
      setDraft(value);
      setEditing(false);
    }
  };

  const toggleReveal = () => {
    if (revealed) {
      setRevealed(false);
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    setRevealed(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    const ms = getRevealTimeout() * 1000;
    timerRef.current = window.setTimeout(() => {
      setRevealed(false);
      timerRef.current = null;
    }, ms);
  };

  if (editing && isEditable) {
    return (
      <NodeViewWrapper
        as="span"
        className="inline-flex items-center align-baseline"
      >
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          className="font-mono text-[13px] rounded px-1.5 py-0.5 align-baseline secret-inline-input"
          style={{
            background: 'var(--bg-surface)',
            color: 'var(--text)',
            WebkitTextFillColor: 'var(--text)',
            caretColor: 'var(--accent)',
            border: '1px solid var(--accent)',
            outline: 'none',
            minWidth: 100,
            boxShadow: '0 0 0 3px var(--accent-bg)',
          }}
          placeholder={t('notes_blocks.secret.placeholder')}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as="span"
      className="inline-flex items-center align-baseline"
    >
      <span
        className="inline-flex items-center gap-1 font-mono text-[13px] rounded px-1.5 py-0.5"
        style={{
          background: 'var(--bg-surface)',
          color: revealed ? 'var(--text)' : 'var(--text-muted)',
          border: '1px solid var(--border)',
        }}
      >
        <span style={{ userSelect: revealed ? 'text' : 'none' }}>
          {revealed ? value : '••••••••'}
        </span>
        <button
          type="button"
          contentEditable={false}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleReveal();
          }}
          className="inline-flex items-center justify-center text-[var(--text-dim)] hover:text-[var(--accent)] -my-0.5"
          aria-label={revealed ? t('notes_blocks.secret.hide') : t('notes_blocks.secret.reveal')}
          title={
            revealed
              ? t('notes_blocks.secret.hide_now')
              : t('notes_blocks.secret.reveal_title', { seconds: getRevealTimeout() })
          }
        >
          <EyeIcon open={revealed} />
        </button>
        {isEditable && (
          <button
            type="button"
            contentEditable={false}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setDraft(value);
              setEditing(true);
            }}
            className="inline-flex items-center justify-center text-[var(--text-dim)] hover:text-[var(--accent)] text-[11px] -my-0.5 px-0.5"
            aria-label={t('common.edit')}
            title={t('notes_blocks.secret.edit_value')}
          >
            ✎
          </button>
        )}
      </span>
    </NodeViewWrapper>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-3.5 h-3.5"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-3.5 h-3.5"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
